<template>
  <div class="site-switcher">
    <el-dropdown @command="handleSiteSwitch" trigger="click">
      <button class="site-btn">
        <i :class="currentSiteIcon"></i>
        <span>{{ currentSiteName }}</span>
        <i class="fas fa-chevron-down"></i>
      </button>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item 
            v-for="site in allSites" 
            :key="site.id"
            :command="site.id"
            :disabled="site.id === currentSiteId"
            :class="{ 'current-site': site.id === currentSiteId }"
          >
            <div class="site-item">
              <i :class="site.icon" class="site-icon"></i>
              <div class="site-details">
                <div class="site-name">{{ getSiteDisplayName(site.id) }}</div>
                <div class="site-url">{{ site.url }}</div>
              </div>
              <div class="site-status">
                <span v-if="site.id === currentSiteId" class="current-badge">{{ t('common.status') }}</span>
                <i v-else class="fas fa-arrow-right switch-icon"></i>
              </div>
            </div>
          </el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <!-- 确认对话框 -->
    <div v-if="showConfirmDialog" class="confirm-dialog-overlay" @click="cancelSwitch">
      <div class="confirm-dialog" @click.stop>
        <div class="confirm-header">
          <h4>{{ t('siteSwitcher.switchSite') }}</h4>
        </div>
        <div class="confirm-content">
          <p>{{ t('siteSwitcher.switchConfirm', { site: targetSite?.name }) }}</p>
          <div class="site-preview">
            <i :class="targetSite?.icon" class="site-icon"></i>
            <span>{{ targetSite?.name }}</span>
            <small>{{ targetSite?.url }}</small>
          </div>
        </div>
        <div class="confirm-actions">
          <button class="btn-cancel" @click="cancelSwitch">
            {{ t('common.cancel') }}
          </button>
          <button class="btn-confirm" @click="confirmSwitch" :disabled="isSwitching">
            <i v-if="isSwitching" class="fas fa-spinner fa-spin"></i>
            {{ isSwitching ? t('siteSwitcher.switching') : t('common.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSiteSwitcher } from '../composables/useSiteSwitcher.js';

const { t } = useI18n();
const {
  currentSiteId,
  allSites,
  isSwitching,
  switchToSite,
  getSiteDisplayName,
  getSiteIcon
} = useSiteSwitcher();

// 响应式数据
const showConfirmDialog = ref(false);
const targetSite = ref(null);

// 当前站点名称和图标（使用国际化）
const currentSiteName = computed(() => {
  const locale = t('language.en') === 'English' ? 'en' : 
                 t('language.zh') === '中文' ? 'zh' : 'ja';
  return getSiteDisplayName(currentSiteId.value, locale);
});

const currentSiteIcon = computed(() => getSiteIcon(currentSiteId.value));

// 处理站点切换
const handleSiteSwitch = (siteId) => {
  if (siteId === currentSiteId.value) {
    return; // 已经是当前站点
  }
  
  const site = allSites.value.find(s => s.id === siteId);
  if (!site) return;
  
  targetSite.value = site;
  showConfirmDialog.value = true;
};

// 确认切换
const confirmSwitch = async () => {
  if (!targetSite.value) return;
  
  try {
    await switchToSite(targetSite.value.id, {
      delay: 500, // 延迟500ms让用户看到切换状态
      saveState: true
    });
  } catch (error) {
    console.error('Site switch failed:', error);
    alert(t('siteSwitcher.switchFailed'));
  }
};

// 取消切换
const cancelSwitch = () => {
  showConfirmDialog.value = false;
  targetSite.value = null;
};
</script>

<style scoped>
.site-switcher {
  display: inline-block;
}

.site-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.site-btn:hover {
  background: rgba(255, 255, 255, 1);
  border-color: rgba(0, 0, 0, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.site-btn i {
  font-size: 12px;
}

.site-icon {
  font-size: 16px;
}

.site-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}

.site-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.site-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.site-url {
  font-size: 12px;
  color: #666;
  font-family: 'Courier New', monospace;
}

.site-status {
  display: flex;
  align-items: center;
}

.current-badge {
  background: #28a745;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
}

.switch-icon {
  color: #666;
  font-size: 12px;
}

:deep(.el-dropdown-menu__item) {
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
}

:deep(.el-dropdown-menu__item:hover) {
  background-color: #f0f2f5;
}

:deep(.el-dropdown-menu__item.current-site) {
  background-color: #e8f5e8;
  color: #28a745;
}

:deep(.el-dropdown-menu__item.current-site:hover) {
  background-color: #d4edda;
}

:deep(.el-dropdown-menu) {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  min-width: 280px;
}

/* 确认对话框样式 */
.confirm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-dialog {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.confirm-header h4 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.1rem;
  font-weight: 600;
}

.confirm-content {
  margin-bottom: 1.5rem;
}

.confirm-content p {
  margin: 0 0 1rem 0;
  color: #666;
  line-height: 1.5;
}

.site-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.site-preview .site-icon {
  color: #007bff;
}

.site-preview span {
  font-weight: 600;
  color: #333;
}

.site-preview small {
  color: #666;
  font-family: 'Courier New', monospace;
  margin-left: auto;
}

.confirm-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
}

.btn-cancel, .btn-confirm {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel {
  background: #6c757d;
  color: white;
}

.btn-cancel:hover {
  background: #5a6268;
}

.btn-confirm {
  background: #007bff;
  color: white;
}

.btn-confirm:hover:not(:disabled) {
  background: #0056b3;
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-confirm i {
  margin-right: 0.5rem;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .confirm-dialog {
    margin: 1rem;
    padding: 1rem;
  }
  
  .site-item {
    gap: 8px;
  }
  
  .site-name {
    font-size: 13px;
  }
  
  .site-url {
    font-size: 11px;
  }
}
</style>
